<template>
  <div :class="containerClass">
    <page-header
      title="Configure Resources"
      :container-class="null"
      class="mb-16pt"
      :info-text="
        getLoggedInUser.role_type === USER_ROLE_TYPES.SCHOOL
          ? `Efficiently organize and manage the Resources tab for easy access to relevant information.`
          : ''
      "
    />

    <div class="col-md-12 px-0 page-section pt-0">
      <b-form-group :label="$t('schoolMsgs.school')" label-for="school" label-class="form-label" v-if="isSuperAdmin">
        <v-select
          id="school"
          class="form-control v-select-custom"
          label="name"
          v-model="school"
          :reduce="school => school.id"
          placeholder="Select School"
          :options="allSchools"
          :loading="areSchoolsLoading"
          @input="prefillLogo(school)"
        >
          <template #search="{ attributes, events }">
            <input class="vs__search" :required="!school" v-bind="attributes" v-on="events" />
          </template>

          <template slot="option" slot-scope="option">
            <div class="d-flex align-items-center">
              <fmv-avatar :title="true" title-class="bg-transparent" rounded no-link slot="aside" size="xs">
                <b-img :src="option.logo_url" class="img-fluid" width="20" alt="Logo" v-if="option.logo_url" />
                <i class="fas fa-university fa-lg" v-else></i>
              </fmv-avatar>
              <span>{{ option.name }}</span>
            </div>
          </template>
          <template slot="selected-option" slot-scope="option">
            {{ option.name }}
          </template>
        </v-select>
        <b-form-invalid-feedback :state="!$v.school.required && $v.school.$dirty ? false : null"
          >This field is required.</b-form-invalid-feedback
        >
      </b-form-group>

      <b-tabs v-model="activeTab" nav-class="nav-tabs-card bg-white px-2" content-class="pt-4">
        <b-tab title="Page Config">
          <div v-if="canManagePageConfig">
            <b-form @submit.prevent="savePageConfig">
              <div class="mb-4">
                <page-separator title="Resource Page Content" />
                <b-form-group label="Page Title" label-for="resource-page-title" label-class="form-label">
                  <b-form-input
                    id="resource-page-title"
                    placeholder="Enter title for the resources page"
                    v-model="resourcePageTitle"
                  />
                </b-form-group>
                <b-form-group label="Page Description" label-for="resource-page-description" label-class="form-label">
                  <b-form-textarea
                    id="resource-page-description"
                    placeholder="Short description shown on the page"
                    :rows="3"
                    v-model="resourcePageDescription"
                  />
                </b-form-group>
              </div>

              <div>
                <page-separator title="Layout" />
                <div class="row">
                  <div class="col-md-6" v-for="layout in resLayoutOptions" :key="layout.image">
                    <div class="layout-hover column text-center clickable-item">
                      <figure>
                        <img
                          width="100%"
                          height="220px"
                          :src="getImgUrl(layout.image)"
                          @click.prevent="() => changeResLayout(layout.value)"
                          :alt="heroLayout"
                          :class="heroLayout === layout.value ? 'border-outline-primary' : ''"
                          :style="
                            heroLayout === layout.value
                              ? 'object-fit: cover;'
                              : 'border: #f2f2f2 2px solid; border-radius: 10px; object-fit: cover;'
                          "
                        />
                      </figure>
                      <p class="mt-2 mb-0 font-weight-semibold">{{ layout.text }}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div class="mt-4">
                <page-separator title="Page Style" />
                <section-style-panel
                  v-model="pageStyle"
                  :capabilities="PAGE_STYLE_CAPABILITIES.resources"
                  :organization="selectedOrganization"
                />
              </div>

              <div class="d-flex justify-content-end mt-3">
                <b-btn variant="primary" type="submit" class="btn-normal" :disabled="isPageConfigSaving">
                  <b-spinner small v-if="isPageConfigSaving" class="mr-2" />
                  <span>{{ isPageConfigSaving ? 'Saving' : 'Save Page Settings' }}</span>
                </b-btn>
              </div>
            </b-form>
          </div>
          <b-alert show variant="light" class="border" v-else>
            Select a school to configure the resources page.
          </b-alert>
        </b-tab>

        <b-tab title="Manage Resources">
          <div v-if="canManageResources">
            <page-separator title="Manage Resources" />

            <div class="d-flex flex-wrap justify-content-end">
              <b-btn
                variant="primary"
                :to="{ name: 'add-resource', query: { ...(school && { school_id: school }) } }"
                style="width: 150px"
                type="button"
                class="btn-normal mb-2"
              >
                <i v-if="isLoading" class="fas fa-circle-notch fa-spin"></i>
                <span v-else>Add Resource</span>
              </b-btn>
            </div>

            <b-skeleton-wrapper :loading="isLoading">
              <template #loading>
                <b-skeleton-table :rows="2" :columns="3" :table-props="{ hover: true }"></b-skeleton-table>
              </template>

              <div v-if="totalResources">
                <b-table
                  :fields="tableColumns"
                  :items="resources"
                  :busy="isLoading"
                  head-variant="light"
                  class="table-nowrap"
                  responsive
                  no-local-sorting
                  @sort-changed="onSortChange"
                >
                  <template #cell(image_url)="data">
                    <fmv-avatar :title="true" title-class="bg-transparent" rounded no-link slot="aside">
                      <b-img
                        :src="data.item.image_url"
                        class="img-fluid"
                        width="40"
                        alt="Logo"
                        v-if="data.item.image_url"
                      />
                      <i class="material-icons" v-else>event</i>
                    </fmv-avatar>
                  </template>
                  <template #cell(title)="data">
                    <strong class="mr-1"> {{ data.item.title }} </strong>
                    <b-badge
                      :class="'border border-' + (data.item.is_featured ? `success text-success` : ``)"
                      variant="none"
                      >{{ data.item.is_featured ? 'Featured' : '' }}</b-badge
                    >
                  </template>

                  <template #cell(is_visible)="data">
                    <b-badge
                      :class="'border border-' + (data.item.is_visible ? `success text-success` : `danger text-danger`)"
                      variant="none"
                      >{{ data.item.is_visible ? 'Active' : 'Inactive' }}</b-badge
                    >
                  </template>
                  <template #cell(is_featured)="data">
                    <b-badge
                      :class="
                        'border border-' + (data.item.is_featured ? `success text-success` : `danger text-danger`)
                      "
                      variant="none"
                      >{{ data.item.is_featured ? 'Featured' : 'Not Featured' }}</b-badge
                    >
                  </template>

                  <template #cell(actions)="data">
                    <a
                      href="#"
                      class="mr-1"
                      @click.prevent="toggleResourceStatus(data.index)"
                      v-b-popover.hover.left
                      :title="data.item.is_visible ? 'Mark Inactive' : 'Mark Active'"
                    >
                      <md-icon :class="data.item.is_visible ? 'text-danger' : 'text-success'">{{
                        data.item.is_visible ? 'block' : 'check_circle_outline'
                      }}</md-icon>
                    </a>
                    <router-link
                      :to="{
                        name: 'edit-resource',
                        params: { id: data.item.id },
                      }"
                      class="svg-icon mr-1"
                      v-b-popover.hover.left
                      :title="$t('generalMsgs.edit')"
                    >
                      <md-icon class="text-info">edit</md-icon>
                    </router-link>
                    <a
                      href="#"
                      @click.prevent="confirmDeleteResource(data.item.id)"
                      v-b-popover.hover.left
                      :title="$t('generalMsgs.remove')"
                    >
                      <md-icon class="text-danger">delete</md-icon>
                    </a>
                  </template>
                </b-table>

                <div class="card-footer">
                  <pagination
                    v-model="currentPage"
                    :total-rows="totalResources"
                    :per-page="perPage"
                    @change="onPageChange"
                    aria-controls="scholarships-table"
                  />
                </div>
              </div>

              <b-card v-else class="text-center">
                <i> There isn't any resources added. Please add a resource if you have any. </i>
              </b-card>
            </b-skeleton-wrapper>
          </div>
          <b-alert show variant="light" class="border" v-else>
            Select a school to manage resources.
          </b-alert>
        </b-tab>
      </b-tabs>
    </div>
  </div>
</template>

<style lang="css" scoped></style>

<script>
import { get, isEmpty } from 'lodash';
import PageSeparator from '@/components/Ui/PageSeparator.vue';
import { mapGetters, mapActions } from 'vuex';
import PageHeader from '@/components/Ui/PageHeader.vue';
import { FmvAvatar } from 'fmv-avatar';
import Page from '@/components/Page.vue';
import { USER_ROLES, USER_ROLE_TYPES, SCHOOL_PAYMENT_PLANS, DEFAULT_PAGE_SIZE } from '@/common/constants';
import { requiredIf } from 'vuelidate/lib/validators';
import Pagination from '@/components/Ui/Pagination.vue';
import vSelect from 'vue-select';
import SectionStylePanel, { SECTION_STYLE_DEFAULTS } from './SectionStylePanel.vue';
import { PAGE_STYLE_CAPABILITIES } from '@/components/WebsiteConfig/pageStyleMixin';
export default {
  components: {
    PageHeader,
    FmvAvatar,
    Pagination,
    vSelect,
    PageSeparator,
    SectionStylePanel,
  },
  extends: Page,
  data() {
    return {
      resources: [],

      SCHOOL_PAYMENT_PLANS,
      title: 'Configure Resources',
      linkedWebsite: null,
      school: null,
      dragging: false,
      isEnable: true,
      options: [
        { item: 'stats', name: 'Stats' },
        { item: 'feature', name: 'Feature' },
      ],
      perPage: DEFAULT_PAGE_SIZE,
      currentPage: 1,
      areSchoolsLoading: false,
      allSchools: [],
      isLoading: false,
      totalResources: null,
      USER_ROLES,
      USER_ROLE_TYPES,
      threeColLayout: false,
      resourcePageTitle: '',
      resourcePageDescription: '',
      activeTab: 0,
      isPageConfigSaving: false,
      PAGE_STYLE_CAPABILITIES,
      pageStyle: { ...SECTION_STYLE_DEFAULTS },
      resLayoutOptions: [
        { text: 'One Column', image: 'res_layout1.png', value: false },
        { text: 'Three Column', image: 'res_layout2.png', value: true },
      ],
    };
  },
  validations() {
    return {
      school: {
        required: requiredIf(() => get(this.getLoggedInUser, 'role') === USER_ROLES.SUPER_ADMIN),
      },
    };
  },

  computed: {
    ...mapGetters('auth', ['getLoggedInUser']),
    isSuperAdmin() {
      return get(this.getLoggedInUser, 'role') === USER_ROLES.SUPER_ADMIN;
    },
    selectedOrganization() {
      if (this.isSuperAdmin) {
        return (this.allSchools || []).find(sch => sch.id === this.school);
      }
      return get(this.getLoggedInUser, 'linked_entity');
    },
    canManagePageConfig() {
      return this.isSuperAdmin ? !!this.school : true;
    },
    canManageResources() {
      return this.canManagePageConfig;
    },
    tableColumns() {
      return [
        { key: 'image_url', label: '' },
        { key: 'title', label: 'Title', tdClass: 'clickable-item' },

        { key: 'is_visible', label: this.$t('generalMsgs.status'), tdClass: 'clickable-item' },

        {
          key: 'actions',
          label: '',
          tdClass: 'text-right',
          thStyle: { minWidth: '100px' },
        },
      ];
    },
    isFormLoading() {
      return this.isLoading || this.areSchoolsLoading;
    },
    heroLayout() {
      return this.threeColLayout;
    },
  },

  methods: {
    ...mapActions('school', ['getAllSchools', 'getSchool']),
    ...mapActions('webConfig', [
      'getResources',
      'updateResourceStatus',
      'deleteResource',
      'updateResPageLayout',
      'getBasicConfig',
    ]),

    get,
    isEmpty,

    prefillLogo(schoolId) {
      if (!schoolId) {
        return;
      }
      this.$router.replace({ name: 'manage-resources', query: { school_id: this.school } });
      this.fetchResources(schoolId);
      this.fetchPageConfiguration(schoolId);
    },

    onPageChange(pageNum) {
      this.fetchResources(pageNum);
    },
    async fetchSchools() {
      this.areSchoolsLoading = true;

      const response = await this.getAllSchools({
        sch_payment_plan__title: `${this.SCHOOL_PAYMENT_PLANS.ADVANCED},${this.SCHOOL_PAYMENT_PLANS.ADVANCED_AND_CHAT}`,
      });
      this.allSchools = response.data;

      this.areSchoolsLoading = false;
    },
    getImgUrl(pic) {
      return require('@/assets/images/webConfig/' + pic);
    },
    async changeResLayout(layout) {
      this.threeColLayout = layout;
    },
    async savePageConfig() {
      if (this.isSuperAdmin) {
        this.$v.school.$touch();
        if (!this.school) {
          this.makeToast({ variant: 'warning', msg: 'Please select a school first.' });
          return;
        }
      }
      await this.persistPageConfig();
    },
    async persistPageConfig({ showToast = true } = {}) {
      this.isPageConfigSaving = true;
      try {
        await this.updateResPageLayout({
          school_id: this.isSuperAdmin ? this.school : null,
          res_three_col_layout: this.threeColLayout,
          resource_page_title: this.resourcePageTitle,
          resource_page_description: this.resourcePageDescription,
          resources_page_style: this.pageStyle,
        });
        if (showToast) {
          this.makeToast({ variant: 'success', msg: 'Resource page settings updated!' });
        }
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      } finally {
        this.isPageConfigSaving = false;
      }
    },
    async fetchPageConfiguration(school_id = null) {
      try {
        const params = this.isSuperAdmin ? { school_id } : {};
        if (this.isSuperAdmin && !school_id) {
          return;
        }
        const config = await this.getBasicConfig(params);
        this.threeColLayout = get(config, 'three_col_res_layout', false);
        this.resourcePageTitle = get(config, 'resource_page_title', '');
        this.resourcePageDescription = get(config, 'resource_page_description', '');
        this.pageStyle = { ...SECTION_STYLE_DEFAULTS, ...(get(config, 'resources_page_style') || {}) };
      } catch (error) {
        // silent fail to avoid blocking page load
      }
    },
    async fetchResources(school_id = null) {
      this.isLoading = true;
      let resp;
      if (school_id) {
        resp = await this.getResources({
          school_id: school_id,
        });
      } else {
        resp = await this.getResources({});
      }
      if (resp && resp.length) {
        const linkedWebsite = get(resp[0], 'linked_website', {});
        if (get(linkedWebsite, 'three_col_res_layout') !== undefined) {
          this.threeColLayout = get(linkedWebsite, 'three_col_res_layout', false);
        }
        this.resourcePageTitle = get(linkedWebsite, 'resource_page_title', this.resourcePageTitle);
        this.resourcePageDescription = get(linkedWebsite, 'resource_page_description', this.resourcePageDescription);
        if (get(linkedWebsite, 'resources_page_style')) {
          this.pageStyle = { ...SECTION_STYLE_DEFAULTS, ...linkedWebsite.resources_page_style };
        }
      }
      this.resources = resp;
      this.totalResources = this.resources.length;

      this.isLoading = false;
    },
    async confirmDeleteResource(id) {
      try {
        const isConfirmed = await this.$bvModal.msgBoxConfirm(
          `Please confirm that you want to remove the selected resource.`,
          {
            title: 'Are you sure?',
            size: 'md',
            buttonSize: 'sm',
            okVariant: 'danger',
            okTitle: 'Yes',
            cancelTitle: 'No',
            footerClass: 'p-2',
            hideHeaderClose: false,
            centered: true,
          }
        );
        if (isConfirmed) {
          await this.deleteResource(id);

          this.fetchResources(
            get(this.getLoggedInUser, 'role') === USER_ROLES.SUPER_ADMIN ? this.$route.query.school_id : null
          );
          this.makeToast({ variant: 'success', msg: 'Resource Successfully Removed' });
        }
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
    },
    async toggleResourceStatus(index) {
      const currStatus = this.resources[index].is_visible;
      this.resources[index].is_visible = !currStatus;

      await this.updateResourceStatus({ id: this.resources[index].id, data: { is_visible: !currStatus } });
      this.makeToast({ variant: 'success', msg: `Resource marked as ${currStatus ? 'Inactive' : 'Active'}` });
    },

    onSortChange(context) {
      this.ordering = context.sortDesc ? '-' + context.sortBy : context.sortBy;
      this.fetchResources();
    },
  },

  async mounted() {
    this.isLoading = true;

    if (this.$route.query.school_id) {
      this.school = parseInt(this.$route.query.school_id);
      await this.fetchResources(this.school);
      await this.fetchPageConfiguration(this.school);
    }

    if (this.isSuperAdmin) {
      this.fetchSchools();
    }

    if (
      get(this.getLoggedInUser, 'role') === USER_ROLES.SCHOOL_OWNER ||
      get(this.getLoggedInUser, 'role') === USER_ROLES.SCHOOL_ADMIN
    ) {
      await this.getSchool(this.getLoggedInUser.linked_entity.id);
      await this.fetchPageConfiguration();
      try {
        await this.fetchResources();
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
    }

    this.isLoading = false;
  },
};
</script>
